export const ALLOWED_ATTACHMENT_MIMES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);
export const MAX_ATTACHMENT_FILE_SIZE = 15 * 1024 * 1024;

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('读取文件失败'));
    reader.readAsDataURL(file);
  });
}

async function uploadAttachmentFile(file) {
  const form = new FormData();
  form.append('file', file);
  const response = await fetch('/api/aichat/upload', {
    method: 'POST',
    credentials: 'same-origin',
    body: form,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `HTTP ${response.status}`);
  }
  return response.json();
}

function normalizeAttachmentName(file) {
  if (file.name && file.name !== 'image.png') return file.name;
  const extension = file.type?.split('/')[1] || 'png';
  const name = `pasted-${Date.now()}.${extension}`;
  Object.defineProperty(file, 'name', { value: name, configurable: true });
  return name;
}

export function createAttachmentController({
  allowedMimes = ALLOWED_ATTACHMENT_MIMES,
  maxFileSize = MAX_ATTACHMENT_FILE_SIZE,
  readDataUrl = readFileAsDataUrl,
  uploadFile = uploadAttachmentFile,
  onStateChange = () => {},
  onUploadError = () => {},
  logger = console,
  setTimeoutImpl = setTimeout,
}) {
  let pendingAttachments = [];
  let uploadingFiles = [];
  let uploadSequence = 0;

  const emitState = () => onStateChange({
    pendingAttachments: [...pendingAttachments],
    uploadingFiles: [...uploadingFiles],
  });

  function getPendingAttachments() {
    return [...pendingAttachments];
  }

  function getState() {
    return {
      pendingAttachments: getPendingAttachments(),
      uploadingFiles: [...uploadingFiles],
    };
  }

  function clearPendingAttachments() {
    if (!pendingAttachments.length) return;
    pendingAttachments = [];
    emitState();
  }

  // AFV-002：发送失败时将附件放回待发区，避免用户重新上传。
  function restorePendingAttachments(list) {
    if (!Array.isArray(list) || !list.length) return;
    const existing = new Set(pendingAttachments.map((item) => item.id));
    const restored = list.filter((item) => item && !existing.has(item.id));
    if (!restored.length) return;
    pendingAttachments = [...pendingAttachments, ...restored];
    emitState();
  }

  function removePendingAttachment(index) {
    if (!Number.isInteger(index) || index < 0 || index >= pendingAttachments.length) return;
    pendingAttachments.splice(index, 1);
    emitState();
  }

  async function processFiles(files) {
    for (const file of files) {
      if (!allowedMimes.has(file.type)) {
        onUploadError(`不支持的文件类型: ${file.type}`);
        continue;
      }
      if (file.size > maxFileSize) {
        onUploadError(`文件过大: ${(file.size / 1024 / 1024).toFixed(1)}MB (最大 ${Math.round(maxFileSize / 1024 / 1024)}MB)`);
        continue;
      }
      const name = normalizeAttachmentName(file);
      const uploadId = `upload-${++uploadSequence}`;
      uploadingFiles.push({ id: uploadId, name, status: 'uploading', error: '' });
      emitState();
      try {
        const dataUrl = await readDataUrl(file);
        const result = await uploadFile(file);
        pendingAttachments.push({
          id: result.id,
          filePath: result.filePath,
          originalName: name,
          mimeType: result.mimeType,
          size: result.size,
          dataUrl,
        });
        uploadingFiles = uploadingFiles.filter((item) => item.id !== uploadId);
        emitState();
      } catch (error) {
        logger.error('[aichat] upload failed:', error);
        uploadingFiles = uploadingFiles.map((item) => item.id === uploadId ? { ...item, status: 'error', error: error.message } : item);
        emitState();
        onUploadError(`上传失败: ${error.message}`);
        setTimeoutImpl(() => {
          uploadingFiles = uploadingFiles.filter((item) => item.id !== uploadId);
          emitState();
        }, 4000);
      }
    }
  }

  return {
    processFiles,
    getPendingAttachments,
    getState,
    clearPendingAttachments,
    restorePendingAttachments,
    removePendingAttachment,
  };
}
